import { Loader2, PanelLeftClose, PanelLeftOpen, Plus, Search, Trash2 } from "lucide-react";
import { type JSX, type MouseEvent, type UIEvent, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import type { ChatPart } from "@/lib/chat-stream";
import type { AgentSummary } from "@/lib/types";
import { cn, shortId } from "@/lib/utils";

export interface ChatSession {
  id: string;
  agent_id: string;
  workspace_id?: string;
  origin: { channel: string; thread_id: string };
  title?: string;
  created_at?: string;
  updated_at: string;
  ended_at?: string;
  run_count?: number;
  last_run_status?: string;
}
export interface ChatHistoryItem {
  role: "user" | "assistant" | "system";
  content: string;
  parts?: ChatPart[];
  /**
   * ISO-8601 UTC instant the turn was authored (`AgentRequest.started_at`
   * for user, `ended_at` for assistant). Plumbed through gateway →
   * runtime → DB; nullable for legacy rows written before the field
   * existed.
   */
  timestamp?: string | null;
  run_id?: string;
}
interface Props {
  agents: AgentSummary[];
  currentAgentId: string | null;
  currentSessionId: string | null;
  /** Open an existing session: caller switches agent + replays history. */
  onOpenSession: (session: ChatSession, history: ChatHistoryItem[]) => void | Promise<void>;
  onNewSession: () => void;
  /**
   * Fires the moment a row click is registered (with the session id)
   * and again with `null` once the history fetch + render is done.
   * Lets the parent paint a loading overlay on the transcript pane
   * synchronously instead of waiting for the network round-trip.
   */
  onOpeningChange?: (sessionId: string | null) => void;
  /** Bumped by the parent after each turn so the rail re-fetches. */
  reloadKey?: number;
  optimisticSession?: ChatSession | null;
}

const RAIL_OPEN_KEY = "knode.chat.rail.open";
const PAGE_SIZE = 20;

function sessionStatus(s: ChatSession): "running" | "ended" | "idle" {
  if (s.ended_at) return "ended";
  if (s.last_run_status === "running") return "running";
  return "idle";
}

function statusVariant(s: ChatSession) {
  const v = sessionStatus(s);
  if (v === "running") return "warn" as const;
  if (v === "ended") return "outline" as const;
  return "ok" as const;
}

/**
 * Match a rail row against an id known to the chat surface.
 *
 * The chat page tracks sessions by the synthetic `sess_<hex>` id the
 * gateway returns from `POST /v1/sessions`; that string lives on the
 * persisted row as `external_thread_key` (surfaced here as
 * `origin.thread_id`). The row's own `id` field is the runtime's UUID.
 * Either value is a legitimate handle for the same conversation, so
 * comparisons must accept both — otherwise the same session shows up
 * twice in the rail (the optimistic placeholder + the refreshed row),
 * which is the bug this helper exists to prevent.
 */
function sessionMatches(s: ChatSession, anyId: string | null | undefined): boolean {
  if (!anyId) return false;
  return s.id === anyId || s.origin.thread_id === anyId;
}

/**
 * Channel-style session list pinned to the left of the chat
 * canvas. Mirrors the lemon_agent web-console rail: search box,
 * "new session" button, status pill per row, soft delete on hover.
 *
 * Backend contract: expects the same `/sessions` endpoints
 * (`GET ?with=stats`, `GET /:id/history`, `DELETE /:id`) the
 * lemon_agent control-plane exposes.
 */
export function ChatSessionsRail({
  agents,
  currentAgentId,
  currentSessionId,
  onOpenSession,
  onNewSession,
  onOpeningChange,
  reloadKey,
  optimisticSession,
}: Props): JSX.Element {
  const [open, setOpen] = useState(true);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  // `null` = never loaded (show skeleton). `false` after the first
  // refresh resolves so we can distinguish "still fetching" from
  // "fetched and empty" — that distinction is what made the empty
  // state previously read as "No sessions yet" while it was actually
  // still in-flight.
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [filter, setFilter] = useState("");
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ChatSession | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const v = localStorage.getItem(RAIL_OPEN_KEY);
      if (v === "0") setOpen(false);
    } catch {
      /* localStorage may be disabled */
    }
  }, []);

  const setOpenPersist = (next: boolean): void => {
    setOpen(next);
    try {
      localStorage.setItem(RAIL_OPEN_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  const refresh = async (): Promise<void> => {
    setInitialLoading(true);
    setHasMore(true);
    try {
      const rows = await api<ChatSession[]>(`/sessions?with=stats&limit=${PAGE_SIZE}&offset=0`);
      setSessions(rows);
      setHasMore(rows.length === PAGE_SIZE);
    } catch {
      setSessions([]);
      setHasMore(false);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadMore = async (): Promise<void> => {
    if (initialLoading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const rows = await api<ChatSession[]>(
        `/sessions?with=stats&limit=${PAGE_SIZE}&offset=${sessions.length}`,
      );
      setSessions((prev) => [...prev, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const onScrollSessions = (e: UIEvent<HTMLDivElement>): void => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      void loadMore();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is the explicit trigger
  useEffect(() => {
    void refresh();
  }, [reloadKey]);

  const agentById = useMemo(() => {
    const m = new Map<string, AgentSummary>();
    for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);

  const agentLabel = (id: string): string => {
    const a = agentById.get(id);
    if (!a) return shortId(id);
    return a.name || shortId(id);
  };

  const visibleSessions = useMemo(() => {
    if (!optimisticSession) return sessions;
    // The optimistic row uses `sess_<hex>` (the gateway-issued id) as
    // both `id` and `origin.thread_id`. The persisted row uses a UUID
    // as `id` and the same `sess_<hex>` as `origin.thread_id`. Match
    // by either so the placeholder is absorbed into the real row
    // instead of stacking on top of it.
    const existingIndex = sessions.findIndex((s) => sessionMatches(s, optimisticSession.id));
    if (existingIndex === -1) return [optimisticSession, ...sessions];
    const merged = [...sessions];
    const existing = merged[existingIndex];
    if (existing && !existing.title && optimisticSession.title) {
      merged[existingIndex] = { ...existing, title: optimisticSession.title };
    }
    return merged;
  }, [sessions, optimisticSession]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: agentById is the source of agentLabel
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return visibleSessions;
    return visibleSessions.filter((s) => {
      const hay = [s.title ?? "", s.origin.thread_id, agentLabel(s.agent_id), s.origin.channel]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [filter, visibleSessions, agentById]);

  const onClickRow = async (s: ChatSession): Promise<void> => {
    if (openingId) return;
    setOpeningId(s.id);
    onOpeningChange?.(s.id);
    try {
      const res = await api<{ items: ChatHistoryItem[] }>(
        `/sessions/${encodeURIComponent(s.id)}/history`,
      );
      await onOpenSession(s, res.items ?? []);
    } catch {
      await onOpenSession(s, []);
    } finally {
      setOpeningId(null);
      onOpeningChange?.(null);
    }
  };

  const requestDelete = (e: MouseEvent, s: ChatSession): void => {
    e.stopPropagation();
    setDeleteError(null);
    setPendingDelete(s);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete || deletingId) return;
    const s = pendingDelete;
    setDeletingId(s.id);
    setDeleteError(null);
    try {
      await api(`/sessions/${encodeURIComponent(s.id)}`, { method: "DELETE" });
      setSessions((prev) => prev.filter((x) => x.id !== s.id));
      setPendingDelete(null);
      if (sessionMatches(s, currentSessionId)) {
        onNewSession();
      }
    } catch (err) {
      setDeleteError(`Could not delete this session: ${String(err)}`);
    } finally {
      setDeletingId(null);
    }
  };

  const deleteTitle = pendingDelete?.title || pendingDelete?.origin.thread_id || "this session";

  const closeDeleteDialog = (nextOpen: boolean): void => {
    if (deletingId) return;
    if (!nextOpen) {
      setPendingDelete(null);
      setDeleteError(null);
    }
  };

  const rail = (() => {
    if (!open) {
      return (
        <aside className="flex h-full w-10 shrink-0 flex-col items-center gap-1 border-r border-border/70 bg-[hsl(var(--list))] py-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground"
                onClick={() => setOpenPersist(true)}
                aria-label="Show sessions"
              >
                <PanelLeftOpen className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Show sessions</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="mt-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-foreground"
                onClick={onNewSession}
                aria-label="New session"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">New session</TooltipContent>
          </Tooltip>
        </aside>
      );
    }

    return (
      <aside className="flex h-full w-64 shrink-0 flex-col overflow-hidden border-r border-border/70 bg-[hsl(var(--list))]">
        <div className="flex items-center gap-1.5 px-2.5 pb-2 pt-2.5">
          <button
            type="button"
            onClick={onNewSession}
            className="inline-flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-2 text-[12px] font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            New session
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-card hover:text-foreground"
                onClick={() => setOpenPersist(false)}
                aria-label="Hide sessions"
              >
                <PanelLeftClose className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">Hide sessions</TooltipContent>
          </Tooltip>
        </div>

        <div className="px-2.5 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter sessions"
              className="h-7 rounded-md border-border/70 bg-background pl-8 text-[11px]"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-1.5 pb-2" onScroll={onScrollSessions}>
          {initialLoading ? (
            // Skeleton list with the same shape as the real rows (title +
            // metadata line + status pill) so the rail doesn't visibly
            // re-flow when the data lands.
            <ul
              aria-busy="true"
              aria-label="Loading sessions"
              className="space-y-0.5 px-0.5 pt-0.5"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton, no semantic identity
                  key={i}
                  className="rounded-md px-2 py-1.5"
                >
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="h-1.5 w-1.5 shrink-0 rounded-full" />
                    <Skeleton className="h-3 w-3/5" />
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 pl-3">
                    <Skeleton className="h-2.5 w-2/5" />
                    <Skeleton className="h-2.5 w-1/5" />
                  </div>
                </li>
              ))}
            </ul>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              {visibleSessions.length === 0
                ? "No sessions yet. Start a new chat above."
                : "No sessions match that filter."}
            </div>
          ) : (
            <>
              <ul className="space-y-0.5">
                {filtered.map((s) => {
                  const active = sessionMatches(s, currentSessionId);
                  const title = s.title || s.origin.thread_id;
                  const label = agentLabel(s.agent_id);
                  const isMatchingAgent = !currentAgentId || currentAgentId === s.agent_id;
                  const status = sessionStatus(s);
                  const opening = openingId === s.id;
                  return (
                    <li key={s.id}>
                      <div
                        className={cn(
                          "group relative rounded-md text-[11px] transition-colors",
                          active ? "bg-primary/10 text-foreground" : "hover:bg-card/80",
                          opening && "bg-primary/5",
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => void onClickRow(s)}
                          title={title}
                          disabled={opening}
                          aria-busy={opening}
                          className="block w-full cursor-pointer rounded-md px-2 py-1.5 pr-14 text-left disabled:cursor-progress"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={cn(
                                  "h-1.5 w-1.5 shrink-0 rounded-full",
                                  active
                                    ? "bg-primary"
                                    : status === "running"
                                      ? "bg-amber-500"
                                      : "bg-muted-foreground/40",
                                )}
                              />
                              <span className="truncate text-[12px] font-medium text-foreground">
                                {title}
                              </span>
                            </div>
                            <div
                              className={cn(
                                "mt-0.5 flex items-center gap-1.5 pl-3 text-[10px]",
                                isMatchingAgent
                                  ? "text-muted-foreground"
                                  : "text-yellow-700 dark:text-[hsl(var(--lemon))]",
                              )}
                            >
                              <span className="truncate">{label}</span>
                              <span>·</span>
                              <span>{s.origin.channel}</span>
                              {typeof s.run_count === "number" && s.run_count > 0 && (
                                <>
                                  <span>·</span>
                                  <span>{s.run_count}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
                          {opening ? (
                            <Loader2
                              className="h-3 w-3 animate-spin text-primary"
                              aria-label="Loading session"
                            />
                          ) : (
                            <Badge variant={statusVariant(s)} className="px-1.5 py-0 text-[9px]">
                              {status}
                            </Badge>
                          )}
                          <button
                            type="button"
                            className="rounded p-1 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                            title="Delete session"
                            onClick={(e) => requestDelete(e, s)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {loadingMore && (
                <div className="flex items-center justify-center py-2 text-[10px] text-muted-foreground">
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  Loading more
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    );
  })();

  return (
    <>
      {rail}
      <Dialog open={pendingDelete !== null} onOpenChange={closeDeleteDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete session?</DialogTitle>
            <DialogDescription>
              This removes <span className="font-medium text-foreground">"{deleteTitle}"</span> from
              the chat history list. The transcript will no longer appear after refresh.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {deleteError}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={deletingId !== null}
              onClick={() => closeDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deletingId !== null}
              onClick={() => void confirmDelete()}
            >
              {deletingId ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Deleting
                </>
              ) : (
                "Delete session"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
